import type { ContactPoint, CodeableConcept, Coding, Extension } from 'fhir/r4';
import * as fhirpath from 'fhirpath';
import { get } from 'lodash';

import {
  buildAddressValidationErrors,
  resolveStateAbbrev,
  validateAddresses,
} from '../services/address-validation';
import { apiClient } from '../services/api-client';
import type { AddressPurpose, AddressValidationItem } from '../types/addressValidation';
import * as FHIRUtils from '../utils/FHIRUtils';

import type { ResourceEntry } from './bundleNarrowing';
import { IMAGE_SOURCE } from './constants';

export function getImage(isMale: boolean, name?: string): string {
  const initial = name ? name.charAt(0).toLowerCase() : 'a';

  // Keyed a-z, so a name starting with a digit or accent returns `undefined` despite
  // the `string` return type. A fallback avatar would change what the `<img>` requests.
  const table: Record<string, string | undefined> = isMale
    ? IMAGE_SOURCE.male
    : IMAGE_SOURCE.female;
  const image = table[initial];
  return image as string;
}

/**
 * Validate a single address against the backend proxy and map the result to
 * the `Promise<true> | reject(string[])` contract used throughout the app.
 *
 * @remarks
 * Despite the name, this makes no direct USPS or Google call — it routes
 * through the backend `/address/validate` endpoint. Use
 * {@link validateMailingAddress} for `purpose: 'mailing'` semantics (no PO Box
 * restriction).
 */
export function validateUSPSAddress(
  addressLine1: string | undefined,
  addressLine2: string | undefined,
  city: string | undefined,
  state: string | undefined,
  zip: string | undefined,
  programId?: string,
) {
  return validateAddressWithPurpose(
    addressLine1,
    addressLine2,
    city,
    state,
    zip,
    'shipping',
    programId,
  );
}

/**
 * Same contract as {@link validateUSPSAddress} but with `purpose: 'mailing'`,
 * which allows PO Boxes.
 */
export function validateMailingAddress(
  addressLine1: string,
  addressLine2: string,
  city: string,
  state: string,
  zip: string,
  programId?: string,
) {
  return validateAddressWithPurpose(
    addressLine1,
    addressLine2,
    city,
    state,
    zip,
    'mailing',
    programId,
  );
}

/*
 * Every parameter admits `undefined`: callers read these off a flattened patient's
 * `string[]` fields, whose first element is absent for a member with no address on
 * file, and validating exactly that case is the point of the call. `addressLines`
 * drops the absent ones below, and `locality` / `administrativeArea` / `postalCode`
 * are already optional on `AddressValidationItem`.
 */
const validateAddressWithPurpose = (
  addressLine1: string | undefined,
  addressLine2: string | undefined,
  city: string | undefined,
  state: string | undefined,
  zip: string | undefined,
  purpose: AddressPurpose,
  programId?: string,
): Promise<true> => {
  return new Promise<true>((resolve, reject) => {
    // `resolveStateAbbrev` returns its argument unchanged for a falsy state, so
    // `''` stays `''` and only a genuinely absent state becomes `undefined`.
    const stateAbbrev = state === undefined ? undefined : resolveStateAbbrev(state);
    const addressLines = [addressLine1, addressLine2].filter((line): line is string =>
      Boolean(line),
    );

    const item: AddressValidationItem = {
      purpose,
      // Payer org id drives program-specific policy (e.g. PO Box bypass).
      ...(programId ? { programId } : {}),
      address: {
        regionCode: 'US',
        addressLines,
        // `exactOptionalPropertyTypes` distinguishes an absent property from one
        // present and `undefined`, and these three are optional on `PostalAddress`.
        // Spreading them in only when set keeps the request body identical to what
        // `JSON.stringify` produced before, which dropped `undefined` values anyway.
        ...(city !== undefined ? { locality: city } : {}),
        ...(stateAbbrev !== undefined ? { administrativeArea: stateAbbrev } : {}),
        ...(zip !== undefined ? { postalCode: zip } : {}),
      },
    };

    validateAddresses([item])
      .then((response) => {
        const result = response?.results?.[0];
        if (!result) {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
          reject(['Unable to validate address']);
          return;
        }

        const messages = buildAddressValidationErrors(result, {
          addressLine1,
          addressLine2,
          city,
          stateAbbrev,
          zip,
        });

        // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
        if (messages.length) reject(messages);
        else resolve(true);
      })
      .catch((error) => {
        // API Gateway non-200 (auth / malformed / batch-size) lands here.
        console.log(error);
        // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
        reject(['Unable to validate address']);
      });
  });
};

/**
 * `patientId` and `org` admit the empty spellings because both catalogue callers
 * (`food/catalog/index.tsx`, `food/catalog/item/foodBundleItems.tsx`) hold them as
 * optional props and pass them straight through. They are only interpolated into the
 * search URL, which is what already happened — an absent id searches for the literal
 * string 'undefined' and matches nothing, rejecting through the `!entry` arm rather
 * than throwing.
 */
export function validatePatientAddress(
  patientId: string | null | undefined,
  org: string | null | undefined,
  authInfoToken: string,
) {
  return new Promise((resolve, reject) => {
    FHIRFetcherOnDemand(`/Patient/?organization=${org}&_id=${patientId}`, authInfoToken).then(
      (data) => {
        if (data && data.entry) {
          // REPORTED, NOT FIXED — see the closing report. `parseFHIRPatient`
          // assigns every `delivery_*` field only inside its
          // `addressItem.type === 'physical'` branch, so a patient carrying no
          // physical address leaves all five genuinely undefined here. Each
          // `[0]` then throws, and the rejection is swallowed into
          // `reject(false)` by the enclosing promise. `validateUSPSAddress`
          // declares all five as required `string`. Asserting keeps that throw;
          // defaulting them would send an empty address to USPS instead.
          //
          // `entry[0]` is asserted for the same reason: the enclosing `data.entry`
          // guard proves the array is present but not that it is non-empty, and a
          // `/Patient/?_id=` search that matched nothing yields `entry: []`. The
          // original indexed it unconditionally, so an empty result already threw
          // here and was already swallowed by the same `reject(false)`. The
          // assertion preserves that throw rather than inventing a branch.
          const patient = FHIRUtils.parseFHIRPatient(data.entry[0]!.resource);
          validateUSPSAddress(
            patient.delivery_addressLine1![0],
            patient.delivery_addressLine2![0],
            // city/state/zip come straight from `fhirpath.evaluate`, whose
            // element type is genuinely unknowable at the declaration, so
            // `FlatPatient` types them `unknown[]`. They are passed on as
            // `string` with no coercion today; the cast says exactly that
            // rather than inventing a String() call.
            patient.delivery_city![0]! as string,
            patient.delivery_state![0]! as string,
            patient.delivery_zip![0]! as string,
            // `?? undefined` is not a default: `programId` reaches the request body
            // only through a `programId ? { programId } : {}` spread, where `null`
            // and `undefined` are already the same "omit it" case.
            org ?? undefined,
          ).then(
            (_success) => {
              resolve(true);
            },
            (_errors) => {
              // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
              reject(false);
            },
          );
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
          reject(false);
        }
      },
      (_error) => {
        // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
        reject(false);
      },
    );
  });
}

/* Helper for parsing the Communication resource.
 *
 * `TComm` is inferred from the constructor argument, so `getResource()` hands back
 * exactly the resource the helper was constructed with. The default is the required
 * `Communication`; sites holding an absent resource write
 * `CommunicationHelper<Communication | undefined>` explicitly. Every method body
 * tolerates an absent resource regardless.
 *
 * The bound stops at `undefined`. `home/messages/emailSMSLog` holds its selected
 * ticket as `Communication | null` and asserts at the two call sites rather than
 * widening the bound, which would weaken it for every other caller.
 */

/**
 * `Array.prototype.indexOf` where the needle may be absent.
 *
 * The identifier setters below all search with `find`, which is `T | undefined`,
 * and hand the result straight to `indexOf`, whose declared parameter is `T`.
 * At runtime `indexOf(undefined)` returns `-1` on an array of objects, which is
 * exactly the "not found" answer those setters branch on — so this states the
 * absent case rather than asserting it away at each of the seven call sites.
 */
function indexOfOptional<T>(haystack: T[] | undefined, needle: T | undefined): number | undefined {
  return haystack?.indexOf(needle as T);
}

export class CommunicationHelper<
  TComm extends fhir4.Communication | undefined = fhir4.Communication,
> {
  comm: TComm;
  constructor(jsonComm: TComm) {
    this.comm = jsonComm;
  }
  getResource(): TComm {
    return this.comm;
  }
  getIdentifiers(): fhir4.Identifier[] | undefined {
    return this.comm?.identifier;
  }
  getIdentifierByCode(code: string): string | undefined {
    return this.getIdentifiers()?.find((i) => i.system === code)?.value;
  }

  addIdentifier(system: string, value: string) {
    if (this.comm?.identifier) {
      this.comm.identifier.push({ system: system, value: value });
    } else {
      // The guard above tested `this.comm?.identifier`, so reaching here means
      // EITHER an absent identifier array on a present resource (the intended
      // case) OR an absent resource, which throws. The assertion keeps both.
      this.comm!.identifier = [{ system: system, value: value }];
    }
  }
  getExtensions(): Extension[] | undefined {
    return this.comm?.extension;
  }
  getExtensionStringByCode(code: string): string | undefined {
    return this.getExtensions()?.find((e) => e.url === code)?.valueString;
  }
  getState(): string | undefined {
    return this.getIdentifiers()?.find(
      (i) => i.system === 'https://projectwell.io/fhir/identifiers/communication/contact-state',
    )?.value;
  }
  setState(state: string): void {
    if (!state) {
      return;
    }
    // `index` is `undefined` when there is no identifier array at all, and
    // `undefined >= 0` is false — so the absent-array case takes the push branch
    // and THROWS on `this.comm.identifier.push`. See the note on
    // setVoiceMailTicketStatus below; this shape preserves all of it.
    const index = indexOfOptional(
      this.getIdentifiers(),
      this.getIdentifiers()?.find((i) => {
        return i.system === 'https://projectwell.io/fhir/identifiers/communication/contact-state';
      }),
    );
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    index! >= 0
      ? (this.comm!.identifier![index!] = {
          system: 'https://projectwell.io/fhir/identifiers/communication/contact-state',
          value: state,
        })
      : this.comm!.identifier!.push({
          system: 'https://projectwell.io/fhir/identifiers/communication/contact-state',
          value: state,
        });
  }
  getCommunicationMediumChannelText(): string | undefined {
    // Two unguarded dereferences: a resource with no `medium` throws on
    // `.filter`, and one whose medium carries no channel coding throws on
    // `[0].text` because filter returned an empty array.
    return this.getMediumV2()!.filter((cc) => {
      return get(cc, 'coding[0].system') === 'https://projectwell.io/fhir/medium/channel';
    })[0]!.text;
  }
  getContactId(): string | undefined {
    return this.getIdentifiers()?.find(
      (i) => i.system === 'https://projectwell.io/fhir/identifiers/communication/contact-id',
    )?.value;
  }
  getDispositionDataExtension(): Extension | undefined {
    return this?.getDispositionExtension()?.extension?.find(
      (ext) =>
        ext?.url ===
        'https://projectwell.io/fhir/zoom-web-hook/extensions/zoom-engagement-disposition-data',
    );
  }
  getDispositionExtension(): Extension | undefined {
    return this?.getExtensions()?.find(
      (ext) =>
        ext?.url ===
          'https://projectwell.io/fhir/zoom-web-hook/extensions/zoom-engagement-disposition' &&
        ext.extension,
    );
  }
  getDispositionDataCodeableConcept(): CodeableConcept | undefined {
    return this?.getDispositionDataExtension()?.valueCodeableConcept;
  }
  getDispositionDataCodeableConceptDisplay(): string | undefined {
    return this?.getDispositionDataCodeableConcept()?.coding?.[0]?.display;
  }
  getMemberPhoneNumber(): string | undefined {
    return this?.getExtensionStringByCode('https://projectwell.io/fhir/member-phone-number');
  }
  getMemberEmail(): string | undefined {
    return this?.getExtensionStringByCode('https://projectwell.io/fhir/member-email');
  }
  getMemberTextNumber(): string | undefined {
    return this?.getExtensionStringByCode('https://projectwell.io/fhir/member-sms-number');
  }
  getOrgEmail(): string | undefined {
    return this.getExtensionStringByCode('https://projectwell.io/fhir/org-email');
  }
  getOrgTextNumber(): string | undefined {
    return this.getExtensionStringByCode('https://projectwell.io/fhir/org-sms-number');
  }
  getSMSMessage(): string | undefined {
    return this.getExtensionStringByCode('https://projectwell.io/fhir/sms-message-body');
  }
  getEmailMessage(): string | undefined {
    return this.getExtensionStringByCode('https://projectwell.io/fhir/email-body');
  }
  getEmailSubject(): string | undefined {
    return this.getExtensionStringByCode('https://projectwell.io/fhir/email-subject');
  }
  getId(): string | undefined {
    return this.comm?.id;
  }
  getSender(): string | undefined {
    return this.comm?.sender?.display ?? 'No name';
  }
  getSubject(): string | undefined {
    return this.comm!.subject?.reference;
  }
  /**
   * `null` as well as `undefined`: all nine call sites are the delete handlers in the
   * communication row components, which pass a literal `null` to clear the subject.
   * Both take the falsy branch below, which deletes the property, so the two spellings
   * were already doing the same thing here.
   */
  setSubject(subject: string | null | undefined): void {
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    subject
      ? // `subject` is dereferenced without a chain, so setting a reference on a
        // Communication that has NO subject throws rather than creating one.
        (this.comm!.subject!.reference = subject)
      : delete this.comm!.subject;
  }
  getTransferredLineNumber(): string | undefined {
    return this.getExtensionStringByCode('https://projectwell.io/fhir/transfer-from-phone-number');
  }
  getCategory(): CodeableConcept[] | undefined {
    return this.comm?.category;
  }
  setCategory(cat: CodeableConcept[]): void {
    this.comm!.category = cat;
  }
  getEngagementId(): string {
    return (
      this.comm!.identifier?.find(
        (id) => id.system === 'https://projectwell.io/fhir/identifiers/communication/engagement-id',
      )?.value || 'N/A'
    );
  }
  getPatientId(): string | undefined {
    return this.getSubject()?.split('/')?.[1];
  }

  // Unlike setState and the -1-testing setters below, these two write at
  // `index` with NO branch on the lookup having succeeded. When the identifier
  // is absent `indexOf` returns -1 and the assignment lands on the array's
  // "-1" property rather than any element — the status is silently not
  // recorded, and `identifier.length` does not change. Preserved as-is.
  setVoiceMailTicketStatus(status: string): void {
    const index = indexOfOptional(
      this.getIdentifiers(),
      this.getIdentifiers()?.find((i) => {
        return (
          i.system ===
          'https://projectwell.io/fhir/identifiers/communication/voicemail-ticket-status'
        );
      }),
    );
    this.comm!.identifier![index!] = {
      system: 'https://projectwell.io/fhir/identifiers/communication/voicemail-ticket-status',
      value: status,
    };
  }
  setTicketStatus(status: string): void {
    const index = indexOfOptional(
      this.getIdentifiers(),
      this.getIdentifiers()?.find((i) => {
        return i.system === 'https://projectwell.io/fhir/identifiers/communication/ticket-status';
      }),
    );
    this.comm!.identifier![index!] = {
      system: 'https://projectwell.io/fhir/identifiers/communication/ticket-status',
      value: status,
    };
  }
  getMedium(): string | undefined {
    return this.comm?.medium?.[0]?.text;
  }
  getMediumV2(): CodeableConcept[] | undefined {
    return this.comm?.medium;
  }
  getOrgId(): string | undefined {
    return this.getIdentifiers()?.find(
      (i) => i.system === 'https://projectwell.io/fhir/identifiers/communication/org-id',
    )?.value;
  }
  getTicketId(): string | undefined {
    return this.getIdentifiers()?.find(
      (i) => i.system === 'https://projectwell.io/fhir/identifiers/communication/ticket-id',
    )?.value;
  }
  getCallerUnkownStatus(): string | undefined {
    return this.getIdentifiers()?.find(
      (i) => i.system === 'https://projectwell.io/fhir/identifiers/communication/unknown-caller',
    )?.value;
  }
  // Branches on `status`, not on the lookup, so this carries the same absent-
  // identifier behaviour as the two ticket-status setters above: a resource
  // with no unknown-caller identifier writes at -1 and the value is dropped.
  setUknownCallerStatus(status: boolean): void {
    const index = indexOfOptional(
      this.getIdentifiers(),
      this.getIdentifiers()?.find((i) => {
        return i.system === 'https://projectwell.io/fhir/identifiers/communication/unknown-caller';
      }),
    );
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    status
      ? (this.comm!.identifier![index!] = {
          system: 'https://projectwell.io/fhir/identifiers/communication/unknown-caller',
          value: 'true',
        })
      : (this.comm!.identifier![index!] = {
          system: 'https://projectwell.io/fhir/identifiers/communication/unknown-caller',
          value: 'false',
        });
  }
  setAbandonedAnsweredId(b: boolean) {
    const identifier = {
      system: 'https://projectwell.io/fhir/identifiers/communication/abandoned-answered',
      value: `${b}`,
    };
    const index = indexOfOptional(
      this.getIdentifiers(),
      this.getIdentifiers()?.find((i) => {
        return (
          i.system === 'https://projectwell.io/fhir/identifiers/communication/abandoned-answered'
        );
      }),
    );
    // This setter DOES test for -1, so a missing identifier is appended rather
    // than lost. `index` is still possibly-undefined when the resource carries
    // no identifier array at all: `undefined === -1` is false, so that case
    // takes the assignment branch and throws.
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    index === -1
      ? this.comm!.identifier!.push(identifier)
      : (this.comm!.identifier![index!] = identifier);
  }
  setIdentifierOrgId(id: string): void {
    const identifier = {
      system: 'https://projectwell.io/fhir/identifiers/communication/org-id',
      value: id,
    };
    const index = indexOfOptional(
      this.getIdentifiers(),
      this.getIdentifiers()?.find((i) => {
        return i.system === 'https://projectwell.io/fhir/identifiers/communication/org-id';
      }),
    );
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    index === -1
      ? this.comm!.identifier!.push(identifier)
      : (this.comm!.identifier![index!] = identifier);
  }
  setVMZoomId(b: boolean) {
    const identifier = {
      system: 'https://projectwell.io/fhir/identifiers/communication/voicemail-answered',
      value: `${b}`,
    };
    const index = indexOfOptional(
      this.getIdentifiers(),
      this.getIdentifiers()?.find((i) => {
        return (
          i.system === 'https://projectwell.io/fhir/identifiers/communication/voicemail-answered'
        );
      }),
    );
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    index === -1
      ? this.comm!.identifier!.push(identifier)
      : (this.comm!.identifier![index!] = identifier);
  }
  getTopic(): string | undefined {
    return this.comm?.topic?.text;
  }
  isVoicemail(): boolean {
    return (
      this.getIdentifierByCode(
        'https://projectwell.io/fhir/identifiers/communication/voicemail',
      ) === 'true'
    );
  }
}
export class TaskHelper {
  task: fhir4.Task | undefined;
  constructor(jsonTask: fhir4.Task | undefined) {
    this.task = jsonTask;
  }
  getIdentifiers(): fhir4.Identifier[] | undefined {
    return this.task?.identifier;
  }
  getConnectId(): string | undefined {
    return this.getIdentifiers()?.find(
      (a) => a?.system === 'https://projectwell.io/fhir/identifiers/contact-id',
    )?.value;
  }
  getCode(): Coding[] | undefined {
    return this.task?.code?.coding;
  }
  getTaskCode(): Coding | undefined {
    return this.getCode()?.find(
      (a) => a?.system === 'https://projectwell.io/fhir/codes/task-codes',
    );
  }
  isVMTask(): boolean {
    return this.getTaskCode()?.code === 'voicemail';
  }

  getStatus(): string | undefined {
    return this.task?.status;
  }

  getStatusReason(): string | undefined {
    return this.task?.statusReason?.text;
  }
}

/* Helper function designated to assist navigatin the patient resource and fields.
 *
 * `TPatient` is inferred from the constructor argument; a site holding an absent
 * resource writes `PatientHelper<Patient | undefined>` explicitly. The default is the
 * required `Patient`.
 *
 * `getId`, `getTelecom` and `getFullName` read `this.patient` WITHOUT the optional
 * chain the other accessors use. That asymmetry is deliberate, and `getFullName`
 * throwing on a patient with no name array is pinned by spec.
 */
//Helper function designated to assist navigatin the patient resource and fields.
export class PatientHelper<TPatient extends fhir4.Patient | undefined = fhir4.Patient> {
  patient: TPatient;
  constructor(jsonPatient: TPatient) {
    this.patient = jsonPatient;
  }
  getId(): string | undefined {
    return this.patient!.id;
  }
  getTelecom(): ContactPoint[] | undefined {
    return this.patient!.telecom;
  }
  getHomePhone(): string | undefined {
    return this.getTelecom()?.find((n) => (n.use = 'home'))?.value;
  }
  getMobilePhone(): string | undefined {
    return this.getTelecom()?.find((n) => (n.use = 'mobile'))?.value;
  }
  hasMobilePhone(): boolean {
    return this.getMobilePhone() !== undefined;
  }
  hasHomePhone(): boolean {
    return this.getHomePhone() !== undefined;
  }
  getFullName(): string | undefined {
    // The non-null assertions record what this expression already does at
    // runtime: it dereferences `name[0]` and `given[0]` unguarded and THROWS on
    // a patient carrying no name array. That throw is pinned by spec, so the
    // absence must stay unhandled — asserting is the only honest way to type it.
    return `${this.patient!.name![0]!.given![0]} ${this.patient!.name![0]!.family}`;
  }
  getExtension(): Extension[] | undefined {
    return this.patient?.extension;
  }
  getExternalReferralCheckbox(): string | undefined {
    return this.getExtension()?.find(
      (e) => e.url === 'https://projectwell.io/fhir/extensions/external-referral',
    )?.valueString;
  }
}

//Helper function designated to assist navigating Questionnaire Response fields - JT
export class QuestionnaireResponseHelper {
  response: fhir4.QuestionnaireResponse | undefined;
  constructor(jsonResponse: fhir4.QuestionnaireResponse | undefined) {
    this.response = jsonResponse;
  }
  isEmpty(): boolean {
    return (this.response?.item?.length ?? 0) <= 0;
  }
  getQResponse(): fhir4.QuestionnaireResponse | undefined {
    return this.response;
  }
  getAuthoredDate(): string | undefined {
    return this.response?.authored;
  }
  getId(): string | undefined {
    return this.response?.id;
  }
  getItemList(): fhir4.QuestionnaireResponseItem[] | undefined {
    return this.response?.item;
  }
  getItemListLength(): number | undefined {
    return this.getItemList()?.length;
  }
  getGroup(index: number): fhir4.QuestionnaireResponseItem | undefined {
    return this.getItemList()?.[index];
  }
  getGroupItem(index: number): fhir4.QuestionnaireResponseItem[] | undefined {
    return this.getGroup(index)?.item;
  }
  getGroupLength(index: number): number | undefined {
    return this.getGroupItem(index)?.length;
  }
  getGroupLinkId(index: number): string | undefined {
    return this.getGroup(index)?.linkId;
  }
  getAnswerItem(
    indexGroup: number,
    indexAnswer: number,
  ): fhir4.QuestionnaireResponseItem | undefined {
    return this.getGroupItem(indexGroup)?.[indexAnswer];
  }
  getAnswerResponse(
    indexGroup: number,
    indexAnswer: number,
  ): fhir4.QuestionnaireResponseItemAnswer[] | undefined {
    return this.getAnswerItem(indexGroup, indexAnswer)?.answer;
  }
  getAnswerLinkId(indexGroup: number, indexAnswer: number): string | undefined {
    return this.getAnswerItem(indexGroup, indexAnswer)?.linkId;
  }
  getExtension(): Extension[] | undefined {
    return this.response?.extension;
  }
  getQuestionnaire(): string | undefined {
    return this.response?.questionnaire;
  }
  getQuestionnaireID(): string | undefined {
    // No optional chain: this THROWS when the response carries no
    // `questionnaire`, which is pinned by spec. The assertion records the
    // existing unguarded dereference rather than handling the absence.
    return this.getQuestionnaire()!.split('/')[1];
  }
  getStatus(): string | undefined {
    return this.response?.status;
  }
  getLastUpdatedTime(): string | undefined {
    return this.response?.meta?.lastUpdated;
  }
}
export class QuestionnaireHelper {
  survey: fhir4.Questionnaire | undefined;
  constructor(jsonQuestionnaire: fhir4.Questionnaire | undefined) {
    this.survey = jsonQuestionnaire;
  }
  isEmpty(): boolean {
    return (this.survey?.item?.length ?? 0) <= 0;
  }
  getTranslation(_lang: string, _ext: []) {}
  getQuestionnaire(): fhir4.Questionnaire | undefined {
    return this.survey;
  }
  getId(): string | undefined {
    return this.survey?.id;
  }
  getItemList(): fhir4.QuestionnaireItem[] | undefined {
    return this.survey?.item;
  }
  getItemListLength(): number | undefined {
    return this.getItemList()?.length;
  }
  getGroup(index: number): fhir4.QuestionnaireItem | undefined {
    // Throws when the questionnaire has no item array (pinned by spec); returns
    // undefined for an out-of-range index. The assertion covers only the first
    // dereference — the index result stays possibly-undefined, as it is.
    return this.getItemList()![index];
  }
  getGroupItem(index: number): fhir4.QuestionnaireItem[] | undefined {
    return this.getGroup(index)?.item;
  }
  getGroupLength(index: number): number | undefined {
    // No optional chain, unlike the QuestionnaireResponseHelper variant: this
    // THROWS for a group with no nested items. Pinned by spec.
    return this.getGroupItem(index)!.length;
  }
  getGroupLinkId(index: number): string | undefined {
    return this.getGroup(index)?.linkId;
  }
  getQuestionItem(indexGroup: number, indexAnswer: number): fhir4.QuestionnaireItem | undefined {
    // Throws when the group carries no nested items, for the same reason as
    // getGroupLength above.
    return this.getGroupItem(indexGroup)![indexAnswer];
  }
  getQuestionLinkId(indexGroup: number, indexAnswer: number): string | undefined {
    return this.getQuestionItem(indexGroup, indexAnswer)?.linkId;
  }
  getSurveyExtension(): Extension[] | undefined {
    return this.survey?.extension;
  }
  getOneSurveyExtension(url: string): Extension | undefined {
    let match: Extension | undefined;
    const extensions = this.survey?.extension;
    extensions?.forEach((e) => {
      if (e.url === url) {
        match = e;
      }
    });
    return match;
  }
  getQuestionExtension(indexGroup: number, indexAnswer: number): Extension[] | undefined {
    return this.getQuestionItem(indexGroup, indexAnswer)?.extension;
  }
  getTitle(): string | undefined {
    return this.survey?.title;
  }
}
//Helper function designated to assist navigating Questionnaire Extension fields - written by JB
export class QuestionnaireExtensionHelper {
  ext: Extension[] | undefined;
  constructor(jsonExtensionArray: Extension[] | undefined) {
    this.ext = jsonExtensionArray;
  }

  isEmpty(): boolean {
    return (this.ext?.length ?? 0) <= 0;
  }
  getExtension(): Extension[] | undefined {
    return this.ext;
  }
  isResourceCreation(): boolean {
    return this.getResourceCreationType() !== undefined;
  }
  isRadioButton(): boolean {
    return this.getItemControlCode() === 'radio-button';
  }
  isSlider(): boolean {
    return this.getItemControlCode() === 'slider';
  }
  isCheckbox(): boolean {
    return this.getItemControlCode() === 'check-box';
  }
  getItemControlCode(): string | undefined {
    const valueCodeableConcept = this.ext?.find(
      (e) => e.url === 'http://hl7.org/fhir/StructureDefinition/questionnaire-itemControl',
    )?.valueCodeableConcept;
    if (!valueCodeableConcept) {
      return undefined;
    }
    // SUSPECTED DEFECT — `CodeableConcept.coding` is `0..*`, so an itemControl
    // extension whose valueCodeableConcept carries only `text` throws here and the
    // questionnaire item fails to render. Reported, not fixed: the assertion keeps
    // that throw, where chaining would return `undefined` and silently fall back to
    // the default control, rendering a slider question as a plain input.
    const coding = valueCodeableConcept.coding!;
    return coding[0]?.code;
  }
  getResourceCreationType(): string | undefined {
    return this.ext?.find((e) => e.url === 'nrx://resource-creation')?.valueString;
  }

  getCompletedDate(): string | undefined {
    return this.ext?.find((e) => e.url === 'https://projectwell.io/fhir/extensions/completed-date')
      ?.valueDateTime;
  }

  getUpdateDate(): string | undefined {
    return this.ext?.find((e) => e.url === 'https://projectwell.io/fhir/extensions/updated-date')
      ?.valueDateTime;
  }
  /**
   * This prefix can optionally be used to add a static prefix to a questionnaire response
   * For example for the answer "Wheat" we may want to prefix it like this "Life Threatening Allergy : Wheat"
   */
  getObservationCreationPrefix() {
    return this.ext?.find((e) => e.url === 'nrx://observation-creation')?.valueString;
  }

  /**
   * If the answer is a coding pass in the code, otherwise pass in the value.
   */
  areConditionsMet(resourceValueOrCode: string | undefined): boolean {
    const maxRangeValue = this.getConditionRangeMaxValue();
    const minRangeValue = this.getConditionRangeMinValue();
    const equalsValue = this.getConditionEqualsValue();
    return (
      // If the extension object is not empty and there are no conditions
      // we'll consider that the conditions have been met.
      (!this.isEmpty() && !maxRangeValue && !minRangeValue && !equalsValue) ||
      // Max value inclusive.
      // An absent `resourceValueOrCode` reaches parseInt here and yields NaN, so the
      // range arms fail. The cast preserves that; defaulting the argument would turn
      // NaN into a real number and change which branch wins.
      (maxRangeValue !== undefined &&
        parseInt(resourceValueOrCode as string) <= parseInt(maxRangeValue)) ||
      // Min value inclusive
      (minRangeValue !== undefined &&
        parseInt(resourceValueOrCode as string) >= parseInt(minRangeValue)) ||
      // equals value
      (equalsValue !== undefined &&
        equalsValue?.toLowerCase() === resourceValueOrCode?.toLowerCase())
    );
  }

  getConditionEqualsValue(): string | undefined {
    return this.ext?.find((e) => e.url === 'nrx://value-equals')?.valueString;
  }

  getConditionRangeMaxValue(): string | undefined {
    return this.ext?.find((e) => e.url === 'nrx://range-max')?.valueString;
  }

  getConditionRangeMinValue(): string | undefined {
    return this.ext?.find((e) => e.url === 'nrx://range-min')?.valueString;
  }

  getUnits(): string | undefined {
    return this.ext?.find((e) => e.url === 'nrx://units-of-measure')?.valueString;
  }

  getPolicyUris(): string[] | undefined {
    const policyUris: string[] = [];

    this.ext?.forEach((element) => {
      if (element.url.toString() === 'nrx://consent-policy-uri') {
        const value = element.valueString;
        if (value) {
          policyUris.push(value);
        }
      }
    });

    return policyUris.length > 0 ? policyUris : undefined;
  }

  getTaskCategory(): string | undefined {
    return this.ext?.find((e) => e.url === 'nrx://task-category')?.valueString;
  }
  //might need future refactoring for multiple translations. only returns first one found
  getTranslations(): Extension[] | undefined {
    return this.ext?.find((e) => e.url === 'http://hl7.org/fhir/StructureDefinition/translation')
      ?.extension;
  }
  hasTranslation(): boolean {
    return this.getTranslations() !== undefined;
  }
  hasTargetTranslation(langCode: string): boolean {
    return this.getTargetTranslation(langCode) !== undefined;
  }
  getTargetTranslation(langCode: string): string | undefined {
    const e = this.ext;
    let value: string | undefined = undefined;
    if (e) {
      for (let i: number = 0; i < e?.length; i++) {
        // `e[i]` is in range by the loop bound — the assertion expresses what
        // the bound already guarantees (noUncheckedIndexedAccess cannot see it).
        // The `extension[0]`/`extension[1]` reads are a different matter: they
        // are unguarded, so a translation extension with fewer than two nested
        // extensions THROWS. Asserting keeps that throw.
        if (e[i]!.url === 'http://hl7.org/fhir/StructureDefinition/translation') {
          const translation = e[i]!;
          if (translation.extension![0]!.valueCode === langCode) {
            value = translation.extension![1]!.valueString;
          }
        }
      }
    }

    return value;
  }
  getInternalTitle(): string | undefined {
    return this.ext?.find((e) => e.url === 'https://projectwell.io/fhir/internal-title')
      ?.valueString;
  }
}
export class CodeableConceptHelper {
  cc: CodeableConcept | undefined;
  constructor(jsonCodeableConcept: CodeableConcept | undefined) {
    this.cc = jsonCodeableConcept;
  }
  isEmpty(): boolean {
    return this.cc === undefined || (this.cc.coding?.length ?? 0) <= 0;
  }
  getCodingList(): Coding[] | undefined {
    return this.cc?.coding;
  }
  getCoding(system: string): Coding | undefined {
    // No chain: a CodeableConcept with no coding array THROWS here.
    return this.getCodingList()!.find((c) => c.system === system);
  }
  getWrapUpCodeCategory(): Coding | undefined {
    const system: string = 'https://projectwell.io/fhir/codes/wrap-up-code-category';
    const code = this.getCoding(system);
    return code;
  }
  getWrapUpCode(): Coding | undefined {
    const system: string = 'https://projectwell.io/fhir/codes/wrap-up-code';
    const code = this.getCoding(system);
    return code;
  }
  getWrapUpCodeSubCode(): Coding | undefined {
    const system: string = 'https://projectwell.io/fhir/codes/wrap-up-code-subcode';
    const code = this.getCoding(system);
    return code;
  }
}
//setCode(): void {
export class CategoryHelper {
  category: CodeableConcept[] | undefined;
  constructor(jsonCodeableArray: CodeableConcept[] | undefined) {
    this.category = jsonCodeableArray;
  }

  isEmpty(): boolean {
    return (this.category?.length ?? 0) <= 0;
  }
  getCategory(): CodeableConcept[] | undefined {
    return this.category;
  }
  // The body already optional-chains `code`, so an absent concept is handled and
  // yields undefined. Widening the parameter to match what the body does costs
  // no behaviour — every caller passes the result of `getCodeableConcept`, which
  // is itself optional.
  getCoding(code: CodeableConcept | undefined, system: string): Coding | undefined {
    return code?.coding?.find((coding) => {
      return coding.system === system;
    });
  }
  getCodeableConcept(system: string): CodeableConcept | undefined {
    return this?.category?.find((code) => this.getCoding(code, system));
  }
  // Every step here is unguarded once `getCodeableConcept` misses: `indexOf`
  // returns -1, `this.category[-1]` is undefined, and the `.coding` read on it
  // THROWS. Nothing tests the lookup, so a category set that lacks the requested
  // system throws rather than no-opping. Assertions record that chain as it is.
  setCodingDisplay(codeDisplay: string, system: string) {
    const code = this.getCodeableConcept(system);
    const indexCategory = this.category?.indexOf(code!);
    const indexCoding = this.category![indexCategory!]!.coding!.indexOf(
      this.getCoding(code, system)!,
    );

    this.category![indexCategory!]!.coding![indexCoding]!.display = codeDisplay;
  }
  getWrapUpCodeCategory(): Coding | undefined {
    const system: string = 'https://projectwell.io/fhir/codes/wrap-up-code-category';
    const code = this.getCodeableConcept(system);
    return this.getCoding(code, system);
  }
  getWrapUpCode(): Coding | undefined {
    const system: string = 'https://projectwell.io/fhir/codes/wrap-up-code';
    const code = this.getCodeableConcept(system);
    return this.getCoding(code, system);
  }
  getWrapUpCodeSubCode(): Coding | undefined {
    const system: string = 'https://projectwell.io/fhir/codes/wrap-up-code-subcode';
    const code = this.getCodeableConcept(system);
    return this.getCoding(code, system);
  }
  setWrapUpCodeCategory(codeDisplay: string): void {
    const system: string = 'https://projectwell.io/fhir/codes/wrap-up-code-category';
    this.setCodingDisplay(codeDisplay, system);
  }
  setWrapUpCode(codeDisplay: string): void {
    const system: string = 'https://projectwell.io/fhir/codes/wrap-up-code';
    this.setCodingDisplay(codeDisplay, system);
  }
  setWrapUpCodeSubCode(codeDisplay: string): void {
    const system: string = 'https://projectwell.io/fhir/codes/wrap-up-code-subcode';
    this.setCodingDisplay(codeDisplay, system);
  }
}

/**
 * `code` and `category` are deliberately unnamed here: they are read through
 * `fhirpath.evaluate`, which flattens both the FHIR-declared
 * `category: CodeableConcept[]` and the bare `{ coding: [...] }` the specs supply.
 * Naming either spelling would reject the other.
 */
export type ObservationEnvelope = {
  resource?:
    | ({
        effectivePeriod?: fhir4.Period | undefined;
        id?: string | undefined;
        valueString?: string | undefined;
      } & Record<string, unknown>)
    | undefined;
  effectivePeriod?: fhir4.Period | undefined;
};

export class ObservationHelper {
  obs: ObservationEnvelope | undefined;
  constructor(obs: ObservationEnvelope | undefined) {
    this.obs = obs;
  }

  getCode(): string | undefined {
    return fhirpath.evaluate(this.obs, 'resource.code.coding.first().code')?.toString();
  }

  getCategoryCode(): string | undefined {
    return fhirpath.evaluate(this.obs, 'resource.category.coding.first().code')?.toString();
  }

  getDueDate(): string | undefined {
    return (
      this.obs?.['resource']?.['effectivePeriod']?.['end'] ??
      this.obs?.['effectivePeriod']?.['start']
    );
  }

  getStartDate(): string | undefined {
    return this.obs?.['resource']?.['effectivePeriod']?.['start'];
  }

  getValueString(): string | undefined {
    return this.obs?.['resource']?.['valueString'];
  }

  getId(): string | undefined {
    return this.obs?.['resource']?.['id'];
  }
}
/**
 * Fetches a FHIR endpoint through the `/fhir` proxy, outside SWR.
 *
 * A local twin of `data/FHIRFetcher`: the two callers here sit inside promise
 * executors rather than components, so they cannot use the SWR fetcher. The type
 * argument is an unchecked claim confined to this boundary.
 *
 * The entry type is narrowed to `ResourceEntry<'Patient'>` because both callers query
 * `/Patient/?…` and hand `entry[0].resource` straight to `parseFHIRPatient`, which
 * takes a `Patient`; the bare `Bundle` leaves that `FhirResource | undefined`. The
 * `entry` array itself stays `0..*`-optional, so the `data.entry` guard above it is
 * load-bearing.
 */
const FHIRFetcherOnDemand = async (endPoint: string, token: string) => {
  const dataEndPoint = '/fhir' + endPoint;

  const data = await apiClient.get<fhir4.Bundle & { entry?: ResourceEntry<'Patient'>[] }>(
    'fhir-proxy',
    dataEndPoint,
    {
      headers: {
        Authorization: 'Bearer ' + token,
        'Content-Type': 'application/json',
      },
    },
  );
  return data;
};
